import { Icon } from '@platform/components';
import { useEffect, useRef, useState } from 'react';

export const SurveyConfirmation = () => {
  const [copied, setCopied] = useState(false);
  const checkmarkRef = useRef<HTMLDivElement>(null);

  const shareUrl = typeof window !== 'undefined' ? window.location.origin + window.location.pathname : '';

  useEffect(() => {
    checkmarkRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const handleCopy = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(shareUrl).catch(() => {});
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  return (
    <div className='max-w-[720px] mx-auto text-center'>
      <div
        ref={checkmarkRef}
        className='w-[60px] h-[60px] mx-auto mb-6 rounded-full bg-secondary-brand-30 text-brand-500 flex items-center justify-center'
      >
        <Icon type='check-circle' size='32' />
      </div>

      <h3 className='text-display-sm font-bold text-gray-700 tracking-tight mb-5'>
        Thank you.
      </h3>

      <p className='text-lg leading-relaxed text-gray-500 max-w-[56ch] mx-auto mb-5'>
        Professionals around the world are sharing their perspective on what we should build next.
        Your input will directly shape the programs we offer and the global impact they create.
      </p>
      <p className='text-lg leading-relaxed text-gray-500 max-w-[56ch] mx-auto'>
        You&rsquo;ll hear from us as we make decisions and prepare to share what&rsquo;s next.
      </p>

      <hr className='border-t border-gray-100 my-11' />

      <h4 className='text-xl font-bold text-gray-700 mb-8'>In the meantime</h4>

      <div className='grid grid-cols-1 gap-4 max-w-[560px] mx-auto text-left'>
        <a
          href='https://movingworlds.org/blog'
          target='_blank'
          rel='noopener noreferrer'
          className='flex flex-col items-start gap-1.5 p-6 border border-gray-100 rounded-xl bg-white no-underline transition-all hover:border-gray-300 hover:shadow-md'
        >
          <span className='w-11 h-11 rounded-lg bg-secondary-brand-30 text-brand-500 flex items-center justify-center mb-1.5'>
            <Icon type='book-open-01' size='22' />
          </span>
          <span className='text-base font-bold text-gray-700 flex items-center gap-1.5'>
            Read stories from our work
            <Icon type='arrow-up-right' stroke='brand-500' size='15' />
          </span>
          <span className='text-sm leading-relaxed text-gray-500'>
            See what social enterprises and professionals are doing through MovingWorlds.
          </span>
        </a>

        <div className='flex flex-col items-start gap-1.5 p-6 border border-gray-100 rounded-xl bg-white'>
          <span className='w-11 h-11 rounded-lg bg-secondary-brand-30 text-brand-500 flex items-center justify-center mb-1.5'>
            <Icon type='share-01' size='22' />
          </span>
          <span className='text-base font-bold text-gray-700'>Share with a colleague</span>
          <span className='text-sm leading-relaxed text-gray-500'>
            Know someone who&rsquo;d want to share their perspective?
          </span>
          <div className='flex flex-col sm:flex-row gap-3 mt-3 w-full'>
            <input
              type='text'
              value={shareUrl}
              readOnly
              aria-label='Share link'
              className='flex-1 min-w-0 h-12 border border-gray-200 rounded-lg bg-gray-50 px-4 font-mono text-sm text-gray-500 focus:outline-none focus:border-brand-500'
            />
            <button
              type='button'
              onClick={handleCopy}
              className='h-12 px-6 border-0 rounded-lg bg-brand-500 text-white text-base font-semibold cursor-pointer whitespace-nowrap transition-colors hover:bg-brand-600 w-full sm:w-auto'
            >
              {copied ? 'Copied!' : 'Copy link'}
            </button>
          </div>
        </div>
      </div>

      <hr className='border-t border-gray-100 my-11' />
    </div>
  );
};
